import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useGetRolesQuery, useUpdateSubscriptionMutation } from 'api/main';
import { promoterRoles, RoleNames } from 'utils/constants';
import { texts } from 'utils/languages/fr';

import { ActionButton, CancelButton, CustomDialogTitle, CustomSelect } from 'components';
import { Dialog, DialogActions, DialogContent, MenuItem } from '@mui/material';
import { getRenderRoleName } from 'utils/helpers';
import { PropTypes } from 'prop-types';

const renderRole = role => (
    <MenuItem key={role.id} value={role.id}>
        {getRenderRoleName(role)}
    </MenuItem>
);

const RoleChangeDialog = ({
    onClose,
    userSubscription,
    open,
    isServicer,
    isProvider,
    isPromoter,
}) => {
    const { data: roles = [] } = useGetRolesQuery();
    const [updateSubscription, { isLoading, isSuccess }] = useUpdateSubscriptionMutation();

    const [selectedRole, setSelectedRole] = useState('');
    const [error, setError] = useState('');

    const authorizedRoles = useMemo(() => {
        if (isProvider) {
            const providerRoles = [
                RoleNames.PROVIDER_ADMIN,
                RoleNames.PROVIDER_SIGNATORY,
                RoleNames.PROVIDER_STANDARD,
            ];
            return providerRoles.filter(role => role !== userSubscription?.role?.name);
        }

        if (isPromoter) {
            return promoterRoles.filter(role => role !== userSubscription?.role?.name);
        }

        return isServicer
            ? [RoleNames.SERVICER_ADMIN, RoleNames.SERVICER_STANDARD, RoleNames.SERVICER_SIGNATORY]
            : [RoleNames.COMMERCIALISATEUR, RoleNames.ADMIN_CIF];
    }, [isPromoter, isServicer, isProvider, userSubscription]);

    const availableRoles = useMemo(
        () =>
            roles.filter(r => {
                return authorizedRoles.includes(r.name) && userSubscription?.role?.id !== r.id;
            }),
        [roles, userSubscription, authorizedRoles]
    );

    const handleChange = useCallback(e => {
        setSelectedRole(+e.target.value);
    }, []);

    const handleConfirm = useCallback(() => {
        if (!selectedRole) {
            setError(texts.CHOISIR_OPTION);
            return;
        }
        setError('');
        updateSubscription({ id: userSubscription.id, role: `api/roles/${selectedRole}` });
    }, [updateSubscription, selectedRole, userSubscription]);

    useEffect(() => {
        if (userSubscription) {
            setSelectedRole(userSubscription?.role?.id);
        }
    }, [userSubscription]);

    useEffect(() => {
        if (isSuccess) {
            onClose();
        }
    }, [isSuccess, onClose]);

    return (
        <Dialog open={open}>
            <CustomDialogTitle title={texts.CHANGE_ROLE} handleClose={isLoading ? null : onClose} />
            <DialogContent>
                <CustomSelect
                    label={texts.SELECTIONNEZ_LE_ROLE}
                    value={selectedRole}
                    onChange={handleChange}
                    error={error}
                    helperText={error}
                >
                    {availableRoles.map(renderRole)}
                </CustomSelect>
            </DialogContent>
            <DialogActions>
                <CancelButton disabled={isLoading} onClick={onClose}>
                    {texts.CANCEL}
                </CancelButton>
                <ActionButton
                    size="small"
                    sx={{ padding: '3px 5px' }}
                    loading={isLoading}
                    disabled={isLoading}
                    onClick={handleConfirm}
                >
                    {texts.CONFIRMER}
                </ActionButton>
            </DialogActions>
        </Dialog>
    );
};

RoleChangeDialog.propTypes = {
    onClose: PropTypes.func.isRequired,
    open: PropTypes.bool,
    isServicer: PropTypes.bool,
    isProvider: PropTypes.bool,
    isPromoter: PropTypes.bool,
    userSubscription: PropTypes.object,
};

export default RoleChangeDialog;
